<template>
    <div class="paginator">
        <button class="paginator-nav" @click="goToFirstPage" :disabled="currentPage === 1" v-tooltip="$t('paginator.first_page')"
            :title="$t('paginator.first_page')">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="17" viewBox="0 0 10 17">
                <path d="M8,1 L8,16 M2,8.5 L8,2 M2,8.5 L8,15" stroke="#aaa" stroke-width="1.5" fill="none"
                    stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
        </button>

        <button class="paginator-nav" @click="previousPage" :disabled="currentPage === 1" v-tooltip="$t('paginator.previous_page')">
            <svg xmlns="http://www.w3.org/2000/svg" width="9.979" height="17.026" viewBox="0 0 9.979 17.026">
                <path d="M3166.006,1871.163l8.95-8.176,1.005,1.37-7.511,6.862,7.536,7.471-1.054,1.323Z"
                    transform="translate(-3166.006 -1862.987)" fill="#aaa"></path>
            </svg>
        </button>

        <div class="page-numbers">
            <button v-for="page in displayedPages" :key="page" @click="gotoPage(page)"
                :class="['page-number', currentPage === page ? 'active' : '']">
                {{ page }}
            </button>
        </div>

        <button class="paginator-nav" @click="nextPage" :disabled="currentPage === totalPages" v-tooltip="$t('paginator.next_page')"
            :title="$t('paginator.next_page')">
            <svg xmlns="http://www.w3.org/2000/svg" width="10.01" height="16.993" viewBox="0 0 10.01 16.993">
                <path d="M3531.023,1871.817l-8.977,8.161-1.009-1.366,7.535-6.85-7.56-7.458,1.058-1.32Z"
                    transform="translate(-3521.012 -1862.985)" fill="#aaa"></path>
            </svg>
        </button>

        <button class="paginator-nav" @click="goToLastPage" :disabled="currentPage === totalPages"
            v-tooltip="$t('paginator.last_page')" :title="$t('paginator.last_page')">

            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="17" viewBox="0 0 10 17">
                <path d="M2,1 L2,16 M8,8.5 L2,2 M8,8.5 L2,15" stroke="#aaa" stroke-width="1.5" fill="none"
                    stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
        </button>

        <select v-if="showPerPage" v-model="itemsPerPage" class="paginator-select">
            <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}/ pag</option>
        </select>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    perPageOptions: {
        type: Array,
        default: () => [10, 20, 30]
    },
    showPerPage: {
        type: Boolean,
        default: false
    },
    totalItems: {
        type: Number,
        default: 100
    },
    currentPage: {
        type: Number,
        default: 1
    }
});

const emit = defineEmits(['page-changed', 'per-page-changed']);
const currentPage = ref(props.currentPage);
const itemsPerPage = ref(props.perPageOptions[0]);

// Watch for external currentPage changes
watch(() => props.currentPage, (newVal) => {
    currentPage.value = newVal;
});

// Calculate total pages based on total items and items per page
const totalPages = computed(() => {
    return Math.ceil(props.totalItems / itemsPerPage.value);
});

// Compute which page numbers to display
const displayedPages = computed(() => {
    // For simplicity, just showing 3 pages as in the image
    // In a full implementation, this would handle more complex logic
    const pages = [];
    const maxVisiblePages = 3;
    let startPage = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2));

    // Adjust start page if near the end
    if (startPage + maxVisiblePages - 1 > totalPages.value) {
        startPage = Math.max(1, totalPages.value - maxVisiblePages + 1);
    }

    // Generate page numbers
    for (let i = 0; i < maxVisiblePages && startPage + i <= totalPages.value; i++) {
        pages.push(startPage + i);
    }

    return pages;
});

function previousPage() {
    if (currentPage.value > 1) {
        const newPage = currentPage.value - 1;
        currentPage.value = newPage;
        emit('page-changed', {
            page: newPage,
            perPage: itemsPerPage.value
        });
    }
}

function nextPage() {
    if (currentPage.value < totalPages.value) {
        const newPage = currentPage.value + 1;
        currentPage.value = newPage;
        emit('page-changed', {
            page: newPage,
            perPage: itemsPerPage.value
        });
    }
}

function goToFirstPage() {
    if (currentPage.value !== 1) {
        currentPage.value = 1;
        emit('page-changed', {
            page: 1,
            perPage: itemsPerPage.value
        });
    }
}

function goToLastPage() {
    if (currentPage.value !== totalPages.value) {
        currentPage.value = totalPages.value;
        emit('page-changed', {
            page: totalPages.value,
            perPage: itemsPerPage.value
        });
    }
}

function gotoPage(page) {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        emit('page-changed', {
            page,
            perPage: itemsPerPage.value
        });
    }
}

// watch(currentPage, (newPage) => {
//     emit('page-changed', {
//         page: newPage,
//         perPage: itemsPerPage.value
//     });
// });

watch(itemsPerPage, (newValue) => {
    emit('per-page-changed', newValue);
    // Reset to page 1 when changing items per page
    currentPage.value = 1;
});

defineExpose({
    itemsPerPage,
    currentPage
});

</script>

<style>
:root {
    /* Colors */
    --color-paginator-page: #666;
    --color-paginator-active-bg: var(--color-orange);
    --color-paginator-active-text: white;
    --color-paginator-nav: #aaa;
    --color-paginator-nav-disabled: #ddd;

    /* Paginator Base */
    --zs-paginator-display: flex;
    --zs-paginator-justify-content: flex-end;
    --zs-paginator-align-items: center;
    --zs-paginator-margin: 10px 0;
    --zs-paginator-font-family: Arial, sans-serif;

    /* Page Numbers Container */
    --zs-paginator-numbers-display: flex;
    --zs-paginator-numbers-margin: 0 10px;

    /* Page Number Button */
    --zs-paginator-page-display: flex;
    --zs-paginator-page-justify-content: center;
    --zs-paginator-page-align-items: center;
    --zs-paginator-page-width: 30px;
    --zs-paginator-page-height: 30px;
    --zs-paginator-page-margin: 0 5px;
    --zs-paginator-page-border: none;
    --zs-paginator-page-background: transparent;
    --zs-paginator-page-border-radius: 4px;
    --zs-paginator-page-cursor: pointer;
    --zs-paginator-page-font-weight: 500;
    --zs-paginator-page-color: var(--color-paginator-page);

    /* Active Page */
    --zs-paginator-active-background: var(--color-paginator-active-bg);
    --zs-paginator-active-color: var(--color-paginator-active-text);

    /* Navigation Buttons */
    --zs-paginator-nav-background: none;
    --zs-paginator-nav-border: none;
    --zs-paginator-nav-cursor: pointer;
    --zs-paginator-nav-padding: 8px;
    --zs-paginator-nav-margin: 0;
    --zs-paginator-nav-color: var(--color-paginator-nav);
    --zs-paginator-nav-disabled-opacity: 0.5;
    --zs-paginator-nav-disabled-cursor: not-allowed;

    /* Select */
    --zs-paginator-select-border: 1px solid #ccc;
    --zs-paginator-select-border-radius: 4px;
    --zs-paginator-select-padding: 5px;
    --zs-paginator-select-margin-left: 10px;
    --zs-paginator-select-font-size: 12px;
    --zs-paginator-select-background: white;
}

.paginator {
    display: var(--zs-paginator-display);
    justify-content: var(--zs-paginator-justify-content);
    align-items: var(--zs-paginator-align-items);
    margin: var(--zs-paginator-margin);
    font-family: var(--zs-paginator-font-family);
}

.page-numbers {
    display: var(--zs-paginator-numbers-display);
    margin: var(--zs-paginator-numbers-margin);
}

.page-number {
    display: var(--zs-paginator-page-display);
    justify-content: var(--zs-paginator-page-justify-content);
    align-items: var(--zs-paginator-page-align-items);
    width: var(--zs-paginator-page-width);
    height: var(--zs-paginator-page-height);
    margin: var(--zs-paginator-page-margin);
    border: var(--zs-paginator-page-border);
    background: var(--zs-paginator-page-background);
    border-radius: var(--zs-paginator-page-border-radius);
    cursor: var(--zs-paginator-page-cursor);
    font-weight: var(--zs-paginator-page-font-weight);
    color: var(--zs-paginator-page-color);
}

.page-number.active {
    background-color: var(--zs-paginator-active-background);
    color: var(--zs-paginator-active-color);
}

.paginator-nav {
    background: var(--zs-paginator-nav-background);
    border: var(--zs-paginator-nav-border);
    cursor: var(--zs-paginator-nav-cursor);
    padding: var(--zs-paginator-nav-padding);
    margin: var(--zs-paginator-nav-margin);
    color: var(--zs-paginator-nav-color);
    display: var(--zs-paginator-nav-display);
    align-items: var(--zs-paginator-nav-align-items);
    justify-content: var(--zs-paginator-nav-justify-content);
}

.paginator-nav:disabled {
    opacity: var(--zs-paginator-nav-disabled-opacity);
    cursor: var(--zs-paginator-nav-disabled-cursor);
}

.paginator-select {
    border: var(--zs-paginator-select-border);
    border-radius: var(--zs-paginator-select-border-radius);
    padding: var(--zs-paginator-select-padding);
    margin-left: var(--zs-paginator-select-margin-left);
    font-size: var(--zs-paginator-select-font-size);
    background-color: var(--zs-paginator-select-background);
}
</style>